import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Button, IconButton, Snackbar, Alert } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchCustomers, deleteCustomer } from "../services/ApiService";
import BulkUploadCustomer from '../components/BulkUploadCustomer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DataTable from '../components/DataTable';

const COLUMNS = [
  { key: 'id', label: 'Customer #' },
  { key: 'name', label: 'Customer' },
  { key: 'contact_person', label: 'Contact Person' },
  { key: 'email', label: 'Email' },
  { key: 'contact_number', label: 'Contact Number' },
  { key: 'business_type', label: 'Business Type' },
  { key: 'corporate', label: 'Corporate' },
  { key: 'plan', label: 'Plan' },
  { key: 'contract_expiry_date', label: 'Contract Expiry' },
  { key: 'billing_cycle', label: 'Billing Cycle' },
  { key: 'billing_contact', label: 'Billing Contact' },
  { key: 'region_city', label: 'Region/City' },
  { key: 'remarks', label: 'Remarks' },
];

export function AddHaulier() {
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = React.useState([]);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteTargetId, setDeleteTargetId] = React.useState(null);

  const handleDeleteClick = (id) => { setDeleteTargetId(id); setDeleteConfirmOpen(true); };
  const confirmDelete = async () => {
    if (deleteTargetId !== null) await handleDelete(deleteTargetId);
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  React.useEffect(() => { loadCustomers(); }, []); // eslint-disable-line

  const loadCustomers = async () => {
    try {
      const response = await fetchCustomers();
      setEmployees(response.data);
      showSnackbar('Customers fetched successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
      showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
    }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  React.useEffect(() => {
    if (location.state?.successMessage) {
      showSnackbar(location.state.successMessage, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = () => {
    const doc = new jsPDF('l', 'mm', 'a2');
    doc.setFontSize(18);
    doc.text('Customer Report', 14, 16);
    const tableData = employees.map(r => [
      r.name, r.contact_person, r.email, r.contact_number,
      r.business_type, r.corporate, r.plan, r.contract_expiry_date,
      r.billing_cycle, r.billing_contact, r.region_city, r.remarks,
    ]);
    const cols = ['Customer','Contact Person','Email','Contact Number','Business Type','Corporate','Plan','Contract Expiry Date','Billing Cycle','Billing Contact','Region/City','Remarks'];
    doc.autoTable({ head: [cols], body: tableData, startY: 30, styles: { fontSize: 9, cellPadding: 3 }, theme: 'grid' });
    doc.save('Customer Report.pdf');
    showSnackbar('PDF exported successfully', 'success');
  };

  const handleDelete = async (id) => {
    try {
      await deleteCustomer(id);
      showSnackbar("Customer deleted successfully", "success");
      loadCustomers();
    } catch (error) {
      showSnackbar("Failed to delete customer", "error");
      console.error(error);
    }
  };


  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <DataTable
        title="Customer List"
        columns={COLUMNS}
        rows={employees}
        toolbarActions={
          <>
            <BulkUploadCustomer />
            <Button variant="contained" color="primary" onClick={() => navigate('/add-customer/new')}>Add Customer</Button>
            <Button variant="contained" color="primary" onClick={handleExport}>Export to PDF</Button>
          </>
        }
        renderActions={(employee) => (
          <>
            <IconButton color="secondary" size="small" onClick={() => navigate(`/add-customer/edit/${employee.id}`, { state: { record: employee } })} sx={{ p: '6px' }}>
              <EditIcon />
            </IconButton>
            <IconButton color="error" size="small" onClick={() => handleDeleteClick(employee.id)} sx={{ p: '6px', ml: '4px' }}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      />
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
